import { Box } from "@mui/material";
import React, { useEffect, useRef } from "react";
// @ts-ignore
import metaObjectEditors from "app/components/common/editor/meta-object-editors";
// @ts-ignore
import componentService from "core/services/componentService/component-service";
// @ts-ignore
import metaObjects from "core/services/metaStoreService/meta-objects";
// @ts-ignore
import { StriimButton } from "@striim/striim-ui";

interface Props {
    componentID: string;
    appName: string;
    prefillWizardData?: Record<string, any>;
    onSave?: (data: Record<string, any>) => void;
    onCancel?: () => void;
}

const BaseSimpleTargetAI: React.FC<Props> = ({ componentID, appName, prefillWizardData, onSave, onCancel }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<any>(null);
    const targetModelRef = useRef<any>(null);
    const onSaveRef = useRef(onSave);
    onSaveRef.current = onSave;

    useEffect(() => {
        if (!containerRef.current || !componentID) return;

        const targetAdapterProps = componentService.getPrefillProperties(componentID);
        const targetType = String(targetAdapterProps.adapter.handler || "");
        targetAdapterProps.adapter.handler = "Global.PROPERTYTEMPLATE." + targetAdapterProps.adapter.handler;

        if (prefillWizardData) {
            Object.assign(targetAdapterProps.adapter.properties, prefillWizardData);
        }

        const targetModel = new metaObjects.TARGET.Model(targetAdapterProps);

        const sanitize = (value: string) => value.replace(/[^A-Z0-9]+/gi, "_");
        const appNamePart = appName ? appName.split(".").pop() ?? "" : "";
        targetModel.set("name", `${sanitize(targetType)}_${sanitize(appNamePart)}_Target`);

        targetModelRef.current = targetModel;

        const form = metaObjectEditors.createForMetadataObject(targetModel, {
            autoSave: true,
            displayFields: ["adapter", "formatter"],
            disableAdapterSelect: true
        });

        form.on("save", () => {
            const model = targetModelRef.current?.toJSON?.();
            onSaveRef.current?.({
                name: model?.name,
                adapter: { name: model?.adapter?.handler, properties: model?.adapter?.properties },
                formatter: { name: model?.formatter?.handler, properties: model?.formatter?.properties }
            });
        });

        form.render();
        containerRef.current.replaceChildren(form.el);
        form.triggerMethod?.("show");
        formRef.current = form;

        return () => {
            const formToCleanup = formRef.current;
            formRef.current = null;
            targetModelRef.current = null;
            // Defer destroy to avoid unmounting React roots embedded in the Marionette view
            // while React is still in its commit phase.
            setTimeout(() => {
                formToCleanup?.destroy();
            }, 0);
        };
    }, [componentID, appName]);

    const handleSave = () => {
        formRef.current?.formBuilder?.onSave();
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
            <Box ref={containerRef} sx={{ flex: 1, backgroundColor: "#fff", p: 2, borderRadius: "8px" }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", pt: 2 }}>
                <StriimButton variant="secondary" size="small" onClick={onCancel}>
                    Cancel
                </StriimButton>
                <StriimButton variant="primary" size="small" onClick={handleSave}>
                    Save
                </StriimButton>
            </Box>
        </Box>
    );
};

export default BaseSimpleTargetAI;
